import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Hash, Calendar, User, Zap, CheckCircle2, AlertTriangle,
  Clock, ShieldCheck, Tag,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Spinner, Button, EmptyState } from "@/components/ui";
import { AssetPhotoGallery } from "@/features/assets/AssetPhotoGallery";

// =============================================================================
// PowerToolDetailScreen — /assets/power-tools/:toolId
// Identity + lifecycle + current test-and-tag state + full history.
// T-055 Commit 3.
// =============================================================================

interface PowerToolFull {
  tool_id:              string;
  tool_code:            string;
  display_name:         string;
  tool_category:        string | null;
  tool_type:            string | null;
  make:                 string | null;
  model:                string | null;
  size_spec:            string | null;
  serial_number:        string | null;
  asset_tag_number:     string | null;
  batch_lot_number:     string | null;
  purchase_date:        string | null;
  purchase_price:       number | null;
  purchase_supplier:    string | null;
  date_of_manufacture:  string | null;
  retirement_date:      string | null;
  retirement_reason:    string | null;
  condition:            string | null;
  is_active:            boolean;
  inspection_required:  boolean | null;
  inspection_interval_months: number | null;
  last_test_and_tag_date: string | null;
  next_test_and_tag_due:  string | null;
  last_test_result:     string | null;
  last_tag_colour:      string | null;
  last_tag_number:      string | null;
  kit_name:             string | null;
  assigned_to_employee_id: string | null;
  assigned_from:        string | null;
  notes:                string | null;
  photo_url:            string | null;
  assigned_to?:  { first_name: string | null; last_name: string | null } | null;
  last_tester?:  { first_name: string | null; last_name: string | null } | null;
}

interface TntRow {
  tnt_id:                  string;
  tested_at:               string;
  tester_licence_number:   string | null;
  test_tag_number:         string | null;
  asset_tag_number:        string | null;
  result:                  string;
  visual_inspection_result: string | null;
  defects_found:           boolean;
  failure_reason:          string | null;
  action_required:         string | null;
  next_test_due:           string | null;
  tag_colour:              string | null;
  notes:                   string | null;
  tester?: { first_name: string | null; last_name: string | null } | null;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${dt.getDate().toString().padStart(2, "0")}/${(dt.getMonth() + 1).toString().padStart(2, "0")}/${dt.getFullYear()}`;
  } catch { return d; }
}

function daysBetween(d: string | null): number | null {
  if (!d) return null;
  const target = new Date(d).getTime();
  const now = new Date().setHours(0, 0, 0, 0);
  return Math.floor((target - now) / (1000 * 60 * 60 * 24));
}

function fullName(e: { first_name: string | null; last_name: string | null } | null | undefined): string | null {
  if (!e) return null;
  const n = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
  return n || null;
}

// Status meaning per MoP: Green = in date, Yellow = due in 15 days, Red = out of date
function testTagStatus(nextDue: string | null): { label: string; tone: "ok" | "warning" | "critical" | "default" } {
  if (!nextDue) return { label: "Never tested", tone: "default" };
  const days = daysBetween(nextDue);
  if (days == null) return { label: "Unknown", tone: "default" };
  if (days < 0)   return { label: `${Math.abs(days)}d overdue`, tone: "critical" };
  if (days <= 15) return { label: `Due in ${days}d`, tone: "warning" };
  return { label: `In date (${days}d)`, tone: "ok" };
}

// -----------------------------------------------------------------------------

export default function PowerToolDetailScreen() {
  const { toolId } = useParams<{ toolId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === "director" || user?.role === "manager";

  const { data: item, isLoading, error } = useQuery({
    queryKey: ["power_tool_detail", toolId],
    enabled: !!toolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("power_tools")
        .select(`
          tool_id, tool_code, display_name, tool_category, tool_type,
          make, model, size_spec, serial_number, asset_tag_number,
          batch_lot_number, purchase_date, purchase_price, purchase_supplier,
          date_of_manufacture, retirement_date, retirement_reason,
          condition, is_active, inspection_required, inspection_interval_months,
          last_test_and_tag_date, next_test_and_tag_due, last_test_result,
          last_tag_colour, last_tag_number,
          kit_name, assigned_to_employee_id, assigned_from, notes, photo_url,
          assigned_to:employees!power_tools_assigned_to_employee_id_fkey(first_name, last_name),
          last_tester:employees!power_tools_last_tester_employee_id_fkey(first_name, last_name)
        `)
        .eq("tool_id", toolId!)
        .single();
      if (error) throw error;
      return data as unknown as PowerToolFull;
    },
  });

  const { data: tntRows = [], isLoading: tntLoading } = useQuery({
    queryKey: ["power_tool_tnt_history", toolId],
    enabled: !!toolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("test_and_tag")
        .select(`
          tnt_id, tested_at, tester_licence_number, test_tag_number,
          asset_tag_number, result, visual_inspection_result, defects_found,
          failure_reason, action_required, next_test_due, tag_colour, notes,
          tester:employees!test_and_tag_tester_employee_id_fkey(first_name, last_name)
        `)
        .eq("power_tool_id", toolId!)
        .order("tested_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as TntRow[];
    },
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (error || !item) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-red-400">
            Power tool not found{error ? `: ${error instanceof Error ? error.message : String(error)}` : "."}
          </div>
        </Card>
      </div>
    );
  }

  const status = testTagStatus(item.next_test_and_tag_due);

  return (
    <div className="p-6 max-w-5xl mx-auto pb-12">
      <button onClick={() => navigate("/assets")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Back to assets
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-text-muted font-mono">{item.tool_code}</div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mt-0.5">
            <Zap size={20} className="text-accent" />
            {item.display_name}
          </h1>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[12px] text-text-muted">
            {item.tool_category && <Pill label={item.tool_category} tone="notice" small />}
            {item.tool_type && <span>{item.tool_type}</span>}
            {item.size_spec && <span>· {item.size_spec}</span>}
            {item.make && <span>· {item.make}</span>}
          </div>
        </div>
        <div className="flex items-start gap-2 flex-wrap">
          {item.inspection_required && (
            <Link to={`/assets/power-tools/${item.tool_id}/test-tag`}>
              <Button icon={CheckCircle2}>New test &amp; tag</Button>
            </Link>
          )}
          {canEdit && <ToggleTestTagButton item={item}/>}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <MiniStat
          icon={ShieldCheck}
          label="Test &amp; tag status"
          value={status.label}
          tone={status.tone === "critical" ? "danger" : status.tone === "warning" ? "warn" : undefined}
        />
        <MiniStat
          icon={Calendar}
          label="Last tested"
          value={fmtDate(item.last_test_and_tag_date)}
          sub={fullName(item.last_tester) ?? item.last_test_result ?? undefined}
        />
        <MiniStat
          icon={Clock}
          label="Next due"
          value={fmtDate(item.next_test_and_tag_due)}
        />
        <MiniStat
          icon={Tag}
          label="Current tag"
          value={item.last_tag_number ?? "—"}
          sub={item.last_tag_colour ?? undefined}
        />
      </div>

      {/* Identity + Lifecycle */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <Card>
          <div className="p-5">
            <SectionLabel icon={Hash}>Identity</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <RO label="Asset tag" value={item.asset_tag_number} mono />
              <RO label="Category" value={item.tool_category} />
              <RO label="Type" value={item.tool_type} />
              <RO label="Size / spec" value={item.size_spec} />
              <RO label="Make" value={item.make} />
              <RO label="Model" value={item.model} />
              <RO label="Serial number" value={item.serial_number} mono />
              <RO label="Batch / lot" value={item.batch_lot_number} mono />
            </dl>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <SectionLabel icon={Calendar}>Lifecycle</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <RO label="Purchase date" value={fmtDate(item.purchase_date)} />
              <RO label="Price" value={item.purchase_price != null ? `$${item.purchase_price.toFixed(2)}` : null} mono />
              <RO label="Supplier" value={item.purchase_supplier} />
              <RO label="Manufactured" value={fmtDate(item.date_of_manufacture)} />
              <RO label="Condition" value={item.condition} />
              <RO label="Test interval" value={item.inspection_interval_months ? `${item.inspection_interval_months} months` : null} />
              <RO label="Retired" value={fmtDate(item.retirement_date)} />
              <RO label="Retirement reason" value={item.retirement_reason} />
            </dl>
          </div>
        </Card>
      </div>

      {/* Assignment */}
      <Card>
        <div className="p-5">
          <SectionLabel icon={User}>Assignment</SectionLabel>
          <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[13px]">
            <RO label="Assigned to" value={fullName(item.assigned_to) ?? "Pool"} />
            <RO label="Assigned from" value={fmtDate(item.assigned_from)} />
            <RO label="Kit / allocation" value={item.kit_name} />
            <RO label="Active" value={item.is_active ? "Yes" : "No"} />
          </dl>
        </div>
      </Card>

      {item.notes && !item.notes.startsWith("[imported") && (
        <Card>
          <div className="p-5">
            <SectionLabel>Notes</SectionLabel>
            <div className="mt-2 text-[13px] text-text whitespace-pre-wrap">{item.notes}</div>
          </div>
        </Card>
      )}

      {/* Photos */}
      <div className="mt-6">
        <AssetPhotoGallery assetKind="power_tool" assetId={item.tool_id} canEdit={canEdit} />
      </div>

      {/* Test & tag history */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] font-medium text-text flex items-center gap-2">
            <ShieldCheck size={15} className="text-accent" />
            Test &amp; tag history
            <span className="text-[11px] text-text-muted font-normal">({tntRows.length})</span>
          </div>
        </div>

        <Card>
          {tntLoading ? (
            <div className="p-6 flex justify-center"><Spinner /></div>
          ) : tntRows.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title={item.inspection_required ? "No tests logged yet" : "Test & tag not required"}
              description={item.inspection_required
                ? "Log the first test using the New test & tag button above."
                : "This item is not flagged for test & tag — it won't appear in the power tool dashboard's due list."}
            />
          ) : (
            <div className="divide-y divide-line">
              {tntRows.map(r => <TntRowCard key={r.tnt_id} r={r} />)}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function SectionLabel({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
      {Icon && <Icon size={12} />}
      {children}
    </div>
  );
}

function RO({ label, value, mono = false }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className={`text-text ${mono ? "font-mono text-[12px]" : ""}`}>
        {value === null || value === undefined || value === "" ? <span className="text-text-dim">—</span> : value}
      </dd>
    </>
  );
}

function MiniStat({ icon: Icon, label, value, sub, tone }: {
  icon: LucideIcon;
  label: string; value: string; sub?: string; tone?: "warn" | "danger";
}) {
  const valueColour = tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : "text-text";
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
          <Icon size={12} />
          {label}
        </div>
        <div className={`mt-1 text-[15px] ${valueColour}`}>{value}</div>
        {sub && <div className="text-[11px] text-text-muted mt-0.5">{sub}</div>}
      </div>
    </Card>
  );
}

function TntRowCard({ r }: { r: TntRow }) {
  const resultLower = (r.result ?? "").toLowerCase();
  const resultTone: "ok" | "warning" | "critical" | "default" =
    resultLower === "pass" ? "ok" :
    resultLower === "fail" ? "critical" : "default";
  const testerName = fullName(r.tester) ?? "Unknown";

  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-mono text-text">{fmtDate(r.tested_at)}</span>
          <Pill label={r.result} tone={resultTone} small />
          {r.visual_inspection_result && r.visual_inspection_result !== r.result && (
            <span className="text-[11px] text-text-muted">Visual: {r.visual_inspection_result}</span>
          )}
          {r.test_tag_number && (
            <span className="inline-flex items-center gap-1 text-[11px] text-text-muted font-mono">
              <Tag size={11} /> {r.test_tag_number}
            </span>
          )}
          {r.defects_found && (
            <span className="inline-flex items-center gap-1 text-[11px] text-warn">
              <AlertTriangle size={11} /> defect
            </span>
          )}
        </div>
        <span className="text-[11px] text-text-muted">
          by {testerName}{r.tester_licence_number ? ` · ${r.tester_licence_number}` : ""}
        </span>
      </div>
      {(r.failure_reason || r.action_required) && (
        <div className="mt-1.5 p-2 rounded bg-warn/10 border border-warn/30 text-[12px]">
          {r.failure_reason && <div className="text-text">{r.failure_reason}</div>}
          {r.action_required && <div className="text-text-muted mt-0.5">Action: {r.action_required}</div>}
        </div>
      )}
      {r.next_test_due && (
        <div className="mt-1 text-[11px] text-text-muted">
          Next due {fmtDate(r.next_test_due)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToggleTestTagButton — manager+ flips inspection_required on the tool.
// Pulls cordless / battery tools out of the Inspection Register without
// retiring them. Confirmed before flipping.
// ---------------------------------------------------------------------------
function ToggleTestTagButton({ item }: { item: { tool_id: string; inspection_required: boolean | null } }) {
  const qc = useQueryClient();
  const isReq = !!item.inspection_required;

  const mut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("power_tools")
        .update({ inspection_required: !isReq })
        .eq("tool_id", item.tool_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["power_tool", item.tool_id] });
      qc.invalidateQueries({ queryKey: ["inspection_register"] });
      qc.invalidateQueries({ queryKey: ["my_equipment"] });
    },
  });

  const handle = () => {
    const msg = isReq
      ? "Mark this tool as cordless/battery (no test &amp; tag)? It'll drop out of the inspection register."
      : "Mark this tool as requiring test &amp; tag? It'll appear back in the inspection register.";
    if (!confirm(msg)) return;
    mut.mutate();
  };

  return (
    <Button
      variant="ghost"
      onClick={handle}
      disabled={mut.isPending}
    >
      {mut.isPending
        ? "Saving…"
        : isReq
          ? "Mark as cordless"
          : "Mark as requires test & tag"}
    </Button>
  );
}
